import { useEffect, useMemo, useRef, useState } from 'react';
import { createStorePerformanceRequest, StorePerformanceInterval } from '../../../../../constants';
import {
  BudgetPlannedData,
  HourlyTurnoverPerformanceItem,
  SalesNetPerformanceItem,
  SCOHourlyTurnoverPerformanceItem,
  useAsyncAction,
  useStorePerformance,
  useStores,
  useTransaction,
  useUser,
} from '../../../../../hooks';
import { useEventContext } from '../../../../../global-state/eventContext';
import { useCelebrateTarget } from '../../../../../hooks/useCelebrateTarget';
import { useCurrencyTools } from '../../../../../hooks/useCurrencyTools';
import { getBudgetAndPlannedPermissions } from '../../../common/budgetPlannedContent/BudgetPlannedContent';
import { getKpiTargetValue, isFailureResponse } from '../../../../../utils';
import {
  SalesNetTabForecastApi,
  buildForecastModel,
  createInitialComparisonMap,
  getSalesNetHasValue,
  getSalesNetTabByIndex,
  mapSalesNetResponseToComparisonMap,
  normalizeNetSaleForecastToSalesNetItems,
  normalizeScoForecastToSalesNetItems,
} from './util';

type UseSalesNetWidgetDataParams = {
  interval: StorePerformanceInterval;
  currentDate: Date;
  isToday: boolean;
  onTargetAchieved: (widgetName: string, target: string, lastUpdated: Date) => void;
};

export const useSalesNetWidgetData = ({
  interval,
  currentDate,
  isToday,
  onTargetAchieved,
}: UseSalesNetWidgetDataParams) => {
  const {
    get: { userPermissions, kpiTargetsProps },
  } = useUser();

  const { fetchSalesNetWidgetData, fetchNetSalesBudget } = useTransaction();
  const { fetchForecastForKpis } = useStores();
  const { transactionEventsCount } = useEventContext();
  const { selectedCurrency, getFormattedCurrency, getConvertedAmount } = useCurrencyTools(interval, currentDate);

  const [tabIndex, setTabIndex] = useState(0);
  const [isBudgetSliderOpen, setIsBudgetSliderOpen] = useState(false);
  const [comparisonValues, setComparisonValues] = useState(createInitialComparisonMap);
  const [activeForecastApiName, setActiveForecastApiName] = useState<SalesNetTabForecastApi>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const cachedNetSalesItems = useRef<SalesNetPerformanceItem[] | null>(null);

  const {
    performanceItems,
    performanceItemsLoading,
    performanceItemsError,
    filteredPerformanceItems,
    forecastPerformanceItems,
    forecastPerformanceItemsLoading,
    triggerGetStorePerformance,
  } = useStorePerformance<SalesNetPerformanceItem>();

  const {
    result: netSaleBudgetPlanned,
    isLoading: netSaleBudgetPlannedLoading,
    isError: netSaleBudgetPlannedError,
    triggerAction: triggerFetchNetSaleBudgetPlanned,
  } = useAsyncAction<BudgetPlannedData>();

  const activeTab = useMemo(() => getSalesNetTabByIndex(tabIndex), [tabIndex]);
  const activeComparison = comparisonValues[activeTab.id];

  const editableTarget = getKpiTargetValue(kpiTargetsProps, 'dailyTurnOver');
  const editableTargetSCO = getKpiTargetValue(kpiTargetsProps, 'dailySCOTurnOver');

  // Fetch budget data for the sales net widget
  const fetchBudget = async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    const response = await fetchNetSalesBudget(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  // Fetch sales net data, optionally using cached data
  const fetchSalesNetData = async (useCache: boolean) => {
    if (useCache) {
      return cachedNetSalesItems.current ?? [];
    }

    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    const response = await fetchSalesNetWidgetData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    setLastUpdated(response.data.lastUpdated);
    setComparisonValues(mapSalesNetResponseToComparisonMap(response.data));

    cachedNetSalesItems.current = response.data.netSales;
    return response.data.netSales;
  };

  // Fetch forecast data based on the active tab's forecast API name
  const fetchForecastData = async () => {
    if (activeTab.forecastApiName === 'netSale') {
      const response = await fetchForecastForKpis<HourlyTurnoverPerformanceItem>('netSale');

      if (isFailureResponse(response)) {
        throw new Error();
      }

      setActiveForecastApiName('netSale');
      return normalizeNetSaleForecastToSalesNetItems(response.data);
    }

    if (activeTab.forecastApiName === 'netSaleSCO') {
      const response = await fetchForecastForKpis<SCOHourlyTurnoverPerformanceItem>('netSaleSCO');

      if (isFailureResponse(response)) {
        throw new Error();
      }

      setActiveForecastApiName('netSaleSCO');
      return normalizeScoForecastToSalesNetItems(response.data);
    }

    setActiveForecastApiName(null);
    return [];
  };

  // Trigger the widget fetch, optionally using cached data
  const triggerWidgetFetch = (useCache: boolean) => {
    const forecastModel = buildForecastModel(activeTab, isToday);

    if (forecastModel.shouldRequestForecast) {
      setActiveForecastApiName(null);
    }

    triggerGetStorePerformance({
      interval,
      currentDate,
      isToday,
      getDataAction: () => fetchSalesNetData(useCache),
      getForecastDataAction: forecastModel.shouldRequestForecast ? fetchForecastData : undefined,
      getHasValue: (item) => getSalesNetHasValue(item, activeTab.metricKey),
    });
  };

  useEffect(() => {
    triggerWidgetFetch(false);
    setIsBudgetSliderOpen(false);
    triggerFetchNetSaleBudgetPlanned(fetchBudget);
  }, [currentDate, interval, transactionEventsCount]);

  useEffect(() => {
    if (cachedNetSalesItems.current === null) {
      return;
    }

    triggerWidgetFetch(true);
  }, [tabIndex]);

  useCelebrateTarget({
    target: editableTarget,
    currentValue: comparisonValues.all.today,
    isToday,
    localStorageKey: 'salesNetTargetAchieved',
    lastUpdated: lastUpdated ?? currentDate,
    onTargetAchieved: (targetSet, updatedDate) => {
      onTargetAchieved('Sales Net', getFormattedCurrency(targetSet), updatedDate);
    },
  });

  useCelebrateTarget({
    target: editableTargetSCO,
    currentValue: comparisonValues.sco.today,
    isToday,
    localStorageKey: 'scoSalesNetTargetAchieved',
    lastUpdated: lastUpdated ?? currentDate,
    onTargetAchieved: (targetSet, updatedDate) => {
      onTargetAchieved('SCO Sales Net', getFormattedCurrency(targetSet), updatedDate);
    },
  });

  const budgetPermissions = getBudgetAndPlannedPermissions(userPermissions);

  const retryWidgetData = () => {
    triggerFetchNetSaleBudgetPlanned(fetchBudget);
    triggerWidgetFetch(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newTabIndex: number) => {
    setTabIndex(newTabIndex);
  };

  return {
    state: {
      tabIndex,
      handleTabChange,
      isBudgetSliderOpen,
      setIsBudgetSliderOpen,
      activeTab,
      activeComparison,
      lastUpdated,
    },
    data: {
      performanceItems,
      performanceItemsLoading,
      performanceItemsError,
      filteredPerformanceItems,
      forecastPerformanceItems,
      forecastPerformanceItemsLoading,
      activeForecastApiName,
    },
    budget: {
      netSaleBudgetPlanned,
      netSaleBudgetPlannedLoading,
      netSaleBudgetPlannedError,
      canViewBudget: budgetPermissions.canViewBudget,
      canViewPlanned: budgetPermissions.canViewPlanned,
      showBudgetAndPlanned: budgetPermissions.showBudgetAndPlanned,
      retryWidgetData,
    },
    format: {
      selectedCurrency,
      getFormattedCurrency,
      getConvertedAmount,
    },
  };
};
